import { useState } from 'react';
import type { App, Environment } from '@hangar/shared';
import type { BuildsSearch } from '@/router';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { AppSelect, BranchInput, EnvironmentSelect, PlatformSelect, StatusFilter } from './BuildsFilterControls';
import { activeFilterCount, isFiltered } from './buildsFiltersModel';

interface Props {
  search: BuildsSearch;
  onChange: (next: BuildsSearch) => void;
  apps: App[] | undefined;
  environments: Environment[];
  branch: string;
  onBranchChange: (v: string) => void;
}

/**
 * Phone filters (malible-premium-ui §2.7): one "Filters" button with the number of filters set, opening a bottom sheet
 * with the same controls as the desktop row, full width and 44 px tall. Every change applies at once (the list behind
 * updates); "Show builds" closes the sheet.
 */
export function BuildsFiltersSheet({ search, onChange, apps, environments, branch, onBranchChange }: Props) {
  const [open, setOpen] = useState(false);
  const count = activeFilterCount(search);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" data-testid="builds-filters-open" className="shrink-0" aria-label={count > 0 ? `Filters, ${count} set` : 'Filters'}>
          <Icon name="tune" size={18} />
          Filters
          {count > 0 ? <span className="tabular rounded-full bg-primary px-1.5 font-mono text-[11px] leading-5 text-primary-foreground">{count}</span> : null}
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" data-testid="builds-filters-sheet" aria-describedby={undefined}>
        <SheetHeader>
          <SheetTitle>Filter builds</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-3 px-4">
          <AppSelect search={search} onChange={onChange} apps={apps} size="default" className="w-full" />
          <EnvironmentSelect search={search} onChange={onChange} environments={environments} size="default" className="w-full" />
          <PlatformSelect search={search} onChange={onChange} size="default" className="w-full" />
          <StatusFilter search={search} onChange={onChange} className="w-full justify-start" />
          <BranchInput value={branch} onChange={onBranchChange} className="w-full" />
        </div>
        <SheetFooter className="flex-row gap-2">
          {isFiltered(search) ? (
            <Button variant="ghost" data-testid="builds-filter-reset" className="flex-1" onClick={() => onChange({})}>
              Reset
            </Button>
          ) : null}
          <Button data-testid="builds-filters-done" className="flex-1" onClick={() => setOpen(false)}>
            Show builds
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
